import React, {FC, useMemo} from 'react';
import style from './style.module.scss';
import {SECTION_CODE, SPECIFICATIONS_SCROLL_NAME} from 'modules/product-card/constants';
import {Box, Col, Row} from 'components/Grid';
import {Typography} from 'components/Typography';
import {Element} from 'react-scroll';
import classNames from 'classnames';
import {useDetailedCharacteristicsListState} from 'modules/product-card/components/DetailedCharacteristicsList/hooks/useDetailedCharacteristicsListState';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectProductData} from 'modules/product-card/selectors';
import {useTasteCharacteristics} from 'modules/product-card/hooks/useTasteCharacteristics';
import {getProductSpecialCategory} from 'modules/product-card/helpers';
import {DetailedCharacteristicsMineralization} from 'modules/product-card/components/DetailedCharacteristicsMineralization';
import {DetailedCharacteristicsTasteList} from 'modules/product-card/components/DetailedCharacteristicsTasteList';
import {DetailedCharacteristicsList} from 'modules/product-card/components/DetailedCharacteristicsList';
import {DetailedDescriptionList} from 'modules/product-card/components/DetailedDescriptionList';
import {BrandLogo} from 'modules/product-card/components/BrandLogo';
import {BrandInfo} from 'modules/product-card/components/BrandInfo';
import {CollapseAnimated} from 'components/CollapseAnimated';
import {CollapseTextInline} from 'components/CollapseTextInline';

// eslint-disable-next-line complexity
const ProductDetailedSpecificationsBlock: FC = () => {
    const productData = useAppSelector(selectProductData);

    const {isShowTaste, tasteList} = useTasteCharacteristics();

    const {isShowAboutBrand, isShowLeftSide, isShowMineralization} = useMemo(() => {
        if (!productData) {
            return {};
        }

        const category = getProductSpecialCategory(productData);

        return {
            isShowAboutBrand: Boolean(productData.brand?.description),
            isShowLeftSide: category !== SECTION_CODE.AKSESSUARY,
            isShowMineralization: category === SECTION_CODE.MINERALNAYA_VODA,
        };
    }, [productData]);

    const {detailedCharacteristics, tabletCharacteristics} = useDetailedCharacteristicsListState();

    const seoText = useMemo(() => {
        const rearLabelValue = productData?.properties?.find((property) => 'rearLabel' === property.code)?.value;

        return rearLabelValue?.toString() ?? '';
    }, [productData?.properties]);

    if (!detailedCharacteristics.length) {
        return null;
    }

    return (
        <div className={style.detailedCharacteristicsContainer}>
            <Element id={SPECIFICATIONS_SCROLL_NAME} name={SPECIFICATIONS_SCROLL_NAME}>
                <Box commonPaddingOnMobileTablet={true}>
                    <Row>
                        <Col>
                            <Typography className={style.title} element={'h2'} variant={'headline-32-m'}>
                                Характеристики
                            </Typography>
                        </Col>
                    </Row>
                    <Row className={style.specificationsRow}>
                        {(isShowMineralization || isShowTaste || 0 < detailedCharacteristics.length) && (
                            <Col
                                className={classNames(style.specificationsCol, isShowLeftSide ? '' : style.hidden)}
                                lg={4}
                                md={12}
                            >
                                {(isShowMineralization || isShowTaste) && (
                                    <div className={style.tasteBlock}>
                                        {isShowMineralization && <DetailedCharacteristicsMineralization />}
                                        {isShowTaste && <DetailedCharacteristicsTasteList list={tasteList} />}
                                    </div>
                                )}
                                <DetailedCharacteristicsList
                                    detailedCharacteristics={detailedCharacteristics}
                                    tabletCharacteristics={tabletCharacteristics}
                                />
                            </Col>
                        )}
                        <Col className={style.descriptionCol} lg={isShowLeftSide ? 8 : 12} md={12}>
                            <DetailedDescriptionList />
                        </Col>
                    </Row>
                    {isShowAboutBrand && (
                        <Row className={style.brandRow}>
                            <Col className={style.brandLogo} lg={4} sm={12}>
                                <BrandLogo />
                            </Col>
                            <Col lg={8} sm={12}>
                                <BrandInfo />
                            </Col>
                        </Row>
                    )}

                    {Boolean(seoText) && (
                        <Row>
                            <Col>
                                <CollapseAnimated>
                                    <Typography
                                        className={classNames(style.blackLabelWrapper, style.seoText)}
                                        color={'gray60'}
                                        element={'div'}
                                    >
                                        <CollapseTextInline
                                            collapsedButtonText={'развернуть'}
                                            expandedButtonText={'свернуть'}
                                            text={seoText}
                                            typographyClassName={style.seoText}
                                        />
                                    </Typography>
                                </CollapseAnimated>
                            </Col>
                        </Row>
                    )}
                </Box>
            </Element>
        </div>
    );
};

export default ProductDetailedSpecificationsBlock;
